import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  REPORT_COLUMN_SPEC,
  REPORT_DEFINITIONS,
  REPORT_SCHEDULE_FIELDS,
  formatReportPeriod,
  formatTimeOfDay,
  parseTimeOfDay,
  payrollPeriodFor,
  shiftPayrollPeriod,
  todayInZone,
  validateReportRun,
  validateSchedule,
  type ReportId,
  type ReportPreview,
  type ReportRunRequest,
  type ReportScheduleInput,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MOBILE, NARROW, SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';

import { ReportPreviewDocument } from './ReportPreviewDocument';

/**
 * The one builder behind BOTH kinds of report (Michael, 2026-10-07): "make it the
 * 'reports' tab where we can either create a scheduled report, or run a one off
 * report ... as someone is creating what the report should include, have a visual to
 * show what the report will look like and what it will contain."
 *
 * 🔥 ONE FORM, TWO OUTCOMES, BECAUSE THE CONTENT IS THE SAME QUESTION. A one-off and a
 * schedule differ only in WHEN and WHERE the file goes — who it covers, which columns
 * and which extra sheets are identical, and are described by the same
 * `ReportSelection` type end to end. Two separate screens would have drifted apart
 * within a month, and the one that drifted would be the one nobody tested.
 *
 * THE PREVIEW IS SERVER-RENDERED, DEBOUNCED, AND NEVER GUESSES. Every edit re-asks the
 * API what this configuration would actually produce. The alternative — composing a
 * sample in the browser from what the form knows — cannot see scope rules, the
 * whole-week overtime window or rate resolution, so it would eventually show a report
 * that does not exist. The cost is a round trip per edit, paid down with a 500 ms
 * debounce and by keeping the last good document on screen while the next one loads
 * (a panel that empties on every keystroke is unreadable).
 *
 * A SCHEDULE'S PERIOD CANNOT BE PREVIEWED EXACTLY, and the screen says so rather than
 * implying otherwise: the runner derives the window from the date it sends, so the
 * preview shows THIS payroll week as the worked example.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** How long to sit still before asking the server again. */
const PREVIEW_DEBOUNCE_MS = 500;

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', ...shorthands.gap('16px'), ...SHRINKABLE },
  header: { display: 'flex', alignItems: 'center', ...shorthands.gap('12px'), flexWrap: 'wrap' },
  split: {
    display: 'grid',
    gridTemplateColumns: 'minmax(300px, 400px) minmax(0, 1fr)',
    ...shorthands.gap('20px'),
    alignItems: 'start',
    ...SHRINKABLE,
    // A form and a document side by side need real width. Below that they stack, form
    // first — you configure, then scroll to what you configured.
    [NARROW]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  form: { display: 'flex', flexDirection: 'column', ...shorthands.gap('12px'), ...SHRINKABLE },
  previewPane: { display: 'flex', flexDirection: 'column', ...shorthands.gap('8px'), ...SHRINKABLE },
  previewHead: { display: 'flex', alignItems: 'center', ...shorthands.gap('8px'), flexWrap: 'wrap' },
  row: { display: 'flex', ...shorthands.gap('12px'), flexWrap: 'wrap' },
  checks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    ...shorthands.gap('2px'),
  },
  presets: { display: 'flex', ...shorthands.gap('6px'), flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  actions: {
    display: 'flex',
    ...shorthands.gap('8px'),
    flexWrap: 'wrap',
    paddingTop: '4px',
    [MOBILE]: { flexDirection: 'column', alignItems: 'stretch' },
  },
  placeholder: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
  },
});

export type ReportBuilderMode = 'schedule' | 'oneOff';

export interface ReportBuilderProps {
  mode: ReportBuilderMode;
  /** The schedule being edited; null for a new schedule or a one-off. */
  scheduleId: string | null;
  draft: ReportScheduleInput;
  onDraftChange: (change: Partial<ReportScheduleInput>) => void;
  employees: ReadonlyArray<{ id: string; name: string }>;
  departments: ReadonlyArray<{ id: string; name: string }>;
  onCancel: () => void;
  /** Save the schedule. Only called in `schedule` mode. */
  onSave: () => void | Promise<void>;
  saving: boolean;
}

/** Plain English, so the preview's byline says where this report is going. */
function describeDelivery(draft: ReportScheduleInput): string {
  const at = formatTimeOfDay(draft.sendAtMinutes);
  const when =
    draft.frequency === 'daily'
      ? `Every day at ${at}`
      : draft.frequency === 'weekly'
        ? `Every ${DAY_NAMES[draft.dayOfWeek ?? 0] ?? '—'} at ${at}`
        : `Day ${String(draft.dayOfMonth ?? 1)} of each month at ${at}`;
  const to = draft.recipients.length === 0 ? 'no recipients yet' : `to ${draft.recipients.join(', ')}`;
  return `${when} ${to}`;
}

/**
 * Save the workbook the server just rendered.
 *
 * base64 → bytes → blob → a click on a link nobody sees. `revokeObjectURL` on the next
 * tick rather than immediately: revoking before the browser has started the download
 * cancels it, and the symptom is a button that silently does nothing.
 */
function saveWorkbook(filename: string, contentType: string, base64: string): void {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

export function ReportBuilder({
  mode,
  scheduleId,
  draft,
  onDraftChange,
  employees,
  departments,
  onCancel,
  onSave,
  saving,
}: ReportBuilderProps): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const today = useMemo(() => todayInZone(new Date()), []);
  const thisWeek = useMemo(() => payrollPeriodFor(today), [today]);

  const [recipientText, setRecipientText] = useState(draft.recipients.join(', '));
  const [timeText, setTimeText] = useState(formatTimeOfDay(draft.sendAtMinutes));
  const [startDate, setStartDate] = useState(thisWeek.startDate);
  const [endDate, setEndDate] = useState(thisWeek.endDate);

  const [preview, setPreview] = useState<ReportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /*
   * A schedule's window is decided by the day it sends, so it cannot be previewed
   * exactly — the current payroll week stands in as the worked example. A one-off is
   * told its window, so it previews precisely what it will produce.
   */
  const windowStart = mode === 'oneOff' ? startDate : thisWeek.startDate;
  const windowEnd = mode === 'oneOff' ? endDate : thisWeek.endDate;

  const request = useMemo<ReportRunRequest>(
    () => ({
      name: draft.name.trim() === '' ? 'Untitled report' : draft.name,
      startDate: windowStart,
      endDate: windowEnd,
      scope: draft.scope,
      employeeIds: [...draft.employeeIds],
      departmentIds: [...draft.departmentIds],
      fields: [...draft.fields],
      sections: [...draft.sections],
    }),
    [draft, windowStart, windowEnd]
  );

  const runProblems = useMemo(() => validateReportRun(request), [request]);
  // In schedule mode the save button answers to the schedule validator — the same pure
  // function the server runs, so the button cannot promise a save the API will refuse.
  const scheduleProblems = useMemo(
    () =>
      mode === 'schedule'
        ? validateSchedule({ ...draft, id: scheduleId ?? 'new', lastRunOn: null })
        : [],
    [mode, draft, scheduleId]
  );
  const problems = mode === 'schedule' ? scheduleProblems : runProblems;

  /*
   * 🔥 THE PREVIEW IS KEYED ON WHAT CHANGES THE ROWS, NOT ON EVERY KEYSTROKE. Renaming
   * a report cannot move a single row, so typing a name must not fire a request per
   * character. The key omits the name; the request itself is read from a ref when the
   * debounce fires, so the name still reaches the server (it heads the document, and
   * an empty one is refused by the validator).
   */
  const previewKey = useMemo(() => JSON.stringify({ ...request, name: '' }), [request]);
  const requestRef = useRef(request);
  requestRef.current = request;
  const blocked = runProblems.length > 0;
  /** Guards against a slow earlier response overwriting a newer one. */
  const sequence = useRef(0);

  useEffect(() => {
    if (blocked) {
      setPreview(null);
      setPreviewError(null);
      setPreviewing(false);
      return undefined;
    }
    const mine = sequence.current + 1;
    sequence.current = mine;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await api.previewReport(requestRef.current);
          if (sequence.current !== mine) {
            return;
          }
          setPreview(result);
          setPreviewError(null);
        } catch (err: unknown) {
          if (sequence.current !== mine) {
            return;
          }
          setPreviewError(
            err instanceof Error ? err.message : 'The preview could not be produced.'
          );
        } finally {
          if (sequence.current === mine) {
            setPreviewing(false);
          }
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [api, previewKey, blocked]);

  const download = useCallback(async (): Promise<void> => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const file = await api.runReportWorkbook(request);
      saveWorkbook(file.filename, file.contentType, file.content);
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : 'The report could not be produced.');
    } finally {
      setDownloading(false);
    }
  }, [api, request]);

  const applyRange = (start: string, end: string): void => {
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>
          {mode === 'oneOff'
            ? 'Run a report'
            : scheduleId === null
              ? 'New scheduled report'
              : 'Edit scheduled report'}
        </Title3>
        <Button appearance="subtle" onClick={onCancel}>
          Back to reports
        </Button>
      </div>

      <div className={styles.split}>
        <div className={styles.form}>
          <Field label="Name">
            <Input
              value={draft.name}
              onChange={(_, d) => {
                onDraftChange({ name: d.value });
              }}
              placeholder={mode === 'oneOff' ? 'Hours for last week' : 'Weekly unapproved hours'}
            />
          </Field>

          {mode === 'oneOff' ? (
            <>
              <div className={styles.row}>
                <Field label="From">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(_, d) => {
                      setStartDate(d.value);
                    }}
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(_, d) => {
                      setEndDate(d.value);
                    }}
                  />
                </Field>
              </div>
              {/*
                The three windows people actually ask for. Payroll runs Wed–Tue, so
                "last week" typed by hand is the single easiest way to produce a report
                that straddles two payroll weeks and quietly restates nobody's week.
              */}
              <div className={styles.presets}>
                <Button
                  size="small"
                  onClick={() => {
                    applyRange(thisWeek.startDate, thisWeek.endDate);
                  }}
                >
                  This payroll week
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    const prev = shiftPayrollPeriod(thisWeek, -1);
                    applyRange(prev.startDate, prev.endDate);
                  }}
                >
                  Last payroll week
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    applyRange(`${today.slice(0, 7)}-01`, today);
                  }}
                >
                  Month to date
                </Button>
              </div>
            </>
          ) : (
            <>
              <Field
                label="Recipients"
                hint="Separate addresses with commas. Delivery still depends on the allowed-senders list."
              >
                <Input
                  value={recipientText}
                  onChange={(_, d) => {
                    setRecipientText(d.value);
                    onDraftChange({
                      recipients: d.value
                        .split(',')
                        .map((x) => x.trim())
                        .filter((x) => x !== ''),
                    });
                  }}
                  placeholder="abbye@jandjbagging.com, michael.sewell@jandjbagging.com"
                />
              </Field>

              <div className={styles.row}>
                <Field label="How often">
                  <Dropdown
                    value={
                      draft.frequency === 'daily'
                        ? 'Daily'
                        : draft.frequency === 'weekly'
                          ? 'Weekly'
                          : 'Monthly'
                    }
                    selectedOptions={[draft.frequency]}
                    onOptionSelect={(_, d) => {
                      const f = d.optionValue as ReportScheduleInput['frequency'];
                      // Clear the field the other frequency does not use, so a schedule
                      // cannot carry a stale day that suddenly means something again.
                      onDraftChange({
                        frequency: f,
                        dayOfWeek: f === 'weekly' ? (draft.dayOfWeek ?? 3) : null,
                        dayOfMonth: f === 'monthly' ? (draft.dayOfMonth ?? 1) : null,
                      });
                    }}
                  >
                    <Option value="daily">Daily</Option>
                    <Option value="weekly">Weekly</Option>
                    <Option value="monthly">Monthly</Option>
                  </Dropdown>
                </Field>

                {draft.frequency === 'weekly' ? (
                  <Field label="Day">
                    <Dropdown
                      value={DAY_NAMES[draft.dayOfWeek ?? 3] ?? 'Wednesday'}
                      selectedOptions={[String(draft.dayOfWeek ?? 3)]}
                      onOptionSelect={(_, d) => {
                        onDraftChange({ dayOfWeek: Number.parseInt(d.optionValue ?? '3', 10) });
                      }}
                    >
                      {DAY_NAMES.map((n, i) => (
                        <Option key={n} value={String(i)}>
                          {n}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                ) : null}

                {draft.frequency === 'monthly' ? (
                  <Field label="Day of month" hint="31 sends on the last day of shorter months.">
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={String(draft.dayOfMonth ?? 1)}
                      onChange={(_, d) => {
                        onDraftChange({ dayOfMonth: Number.parseInt(d.value, 10) });
                      }}
                    />
                  </Field>
                ) : null}

                <Field label="Time" hint="e.g. 7:00am">
                  <Input
                    value={timeText}
                    onChange={(_, d) => {
                      setTimeText(d.value);
                      const parsed = parseTimeOfDay(d.value);
                      if (parsed !== null) {
                        onDraftChange({ sendAtMinutes: parsed });
                      }
                    }}
                    onBlur={() => {
                      setTimeText(formatTimeOfDay(draft.sendAtMinutes));
                    }}
                  />
                </Field>
              </div>
            </>
          )}

          <Field label="Who it covers">
            <Dropdown
              value={
                draft.scope === 'all'
                  ? 'All employees'
                  : draft.scope === 'supervisor'
                    ? 'All employees, split by supervisor'
                    : 'Selected employees'
              }
              selectedOptions={[draft.scope]}
              onOptionSelect={(_, d) => {
                onDraftChange({ scope: d.optionValue as ReportScheduleInput['scope'] });
              }}
            >
              <Option value="all">All employees</Option>
              <Option value="supervisor">All employees, split by supervisor</Option>
              <Option value="selected">Selected employees</Option>
            </Dropdown>
          </Field>

          {/*
            BY DEPARTMENT **OR** BY NAME, because both are real weekly questions
            ("everyone in Shipping" vs "these four people") and expressing the first
            as a list of names goes stale the moment somebody moves department.
          */}
          {draft.scope === 'selected' ? (
            <>
              <Field
                label="Departments"
                hint="Everyone in these departments. Leave empty to pick people by name instead."
              >
                <Dropdown
                  multiselect
                  value={
                    draft.departmentIds.length === 0
                      ? 'None'
                      : `${String(draft.departmentIds.length)} selected`
                  }
                  selectedOptions={[...draft.departmentIds]}
                  onOptionSelect={(_, d) => {
                    onDraftChange({ departmentIds: [...d.selectedOptions] });
                  }}
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Employees" hint="Named individuals, on top of any departments above.">
                <Dropdown
                  multiselect
                  value={
                    draft.employeeIds.length === 0
                      ? 'None'
                      : `${String(draft.employeeIds.length)} selected`
                  }
                  selectedOptions={[...draft.employeeIds]}
                  onOptionSelect={(_, d) => {
                    onDraftChange({ employeeIds: [...d.selectedOptions] });
                  }}
                >
                  {employees.map((e) => (
                    <Option key={e.id} value={e.id}>
                      {e.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </>
          ) : null}

          <Field label="Time entry columns">
            <div className={styles.checks}>
              {REPORT_SCHEDULE_FIELDS.map((f) => (
                <Checkbox
                  key={f}
                  label={REPORT_COLUMN_SPEC[f].label}
                  checked={draft.fields.includes(f)}
                  onChange={(_, d) => {
                    onDraftChange({
                      fields: d.checked
                        ? [...draft.fields, f]
                        : draft.fields.filter((x) => x !== f),
                    });
                  }}
                />
              ))}
            </div>
          </Field>

          <Field label="Also include">
            <div className={styles.checks}>
              {REPORT_DEFINITIONS.map((def) => (
                <Checkbox
                  key={def.id}
                  label={def.label}
                  checked={draft.sections.includes(def.id)}
                  onChange={(_, d) => {
                    onDraftChange({
                      sections: d.checked
                        ? [...draft.sections, def.id]
                        : draft.sections.filter((x: ReportId) => x !== def.id),
                    });
                  }}
                />
              ))}
            </div>
          </Field>

          {mode === 'schedule' ? (
            <Checkbox
              label="Active — send this report automatically"
              checked={draft.active}
              onChange={(_, d) => {
                onDraftChange({ active: d.checked === true });
              }}
            />
          ) : null}

          {problems.length > 0 ? (
            <MessageBar intent="warning">
              <MessageBarBody>{problems.map((p) => p.message).join(' ')}</MessageBarBody>
            </MessageBar>
          ) : null}
          {downloadError !== null ? (
            <MessageBar intent="error">
              <MessageBarBody>{downloadError}</MessageBarBody>
            </MessageBar>
          ) : null}

          <div className={styles.actions}>
            {mode === 'schedule' ? (
              <Button
                appearance="primary"
                disabled={saving || problems.length > 0}
                onClick={() => void onSave()}
              >
                {saving ? <Spinner size="tiny" /> : 'Save schedule'}
              </Button>
            ) : (
              <Button
                appearance="primary"
                disabled={downloading || problems.length > 0}
                onClick={() => void download()}
              >
                {downloading ? <Spinner size="tiny" /> : 'Download spreadsheet'}
              </Button>
            )}
            <Button onClick={onCancel}>Cancel</Button>
          </div>

          {mode === 'oneOff' ? (
            <Text className={styles.muted}>
              A one-off report is downloaded to this computer. It is not emailed and it
              does not affect any scheduled report.
            </Text>
          ) : null}
        </div>

        <div className={styles.previewPane}>
          <div className={styles.previewHead}>
            <Text weight="semibold">Preview</Text>
            {previewing ? <Spinner size="tiny" label="Updating…" labelPosition="after" /> : null}
          </div>

          {previewError !== null ? (
            <MessageBar intent="error">
              <MessageBarBody>{previewError}</MessageBarBody>
            </MessageBar>
          ) : null}

          {preview === null ? (
            <div className={styles.placeholder}>
              <Text>
                {runProblems.length > 0
                  ? 'Finish the settings on the left and the preview will appear here.'
                  : 'Building the preview…'}
              </Text>
            </div>
          ) : (
            <ReportPreviewDocument
              preview={preview}
              deliveryLine={
                mode === 'oneOff'
                  ? 'Downloaded as a spreadsheet'
                  : describeDelivery(draft)
              }
              periodNote={
                mode === 'schedule'
                  ? `Each send covers the payroll week it goes out in. Shown here for the ` +
                    `current week, ${formatReportPeriod(thisWeek.startDate, thisWeek.endDate)}.`
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
